import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getCreatePhysicalSocketDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-physical-socket.form-default-values';
import { CreatePhysicalSocketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-physical-socket.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoCreatePhysicalSocketMutation } from '@service/cpms-bo-charge-point.service';

import {
  CreateChargePointChannelPhysicalSocketContextProvider,
  useChargePointChannelContext,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointChannelPhysicalSocketCreateFormController } from './charge-point-channel-physical-socket-create-form/charge-point-channel-physical-socket-create-form.controller';

interface ChargePointChannelPhysicalSocketCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointChannelPhysicalSocketCreateController = ({
  onCancel,
  onSuccess,
}: ChargePointChannelPhysicalSocketCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [createPhysicalSocketMutation] =
    useCpmsBoCreatePhysicalSocketMutation();
  const chargePoint = useChargePointContext();
  const channel = useChargePointChannelContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<
      CreatePhysicalSocketModel,
      'chargePointId' | 'channelId' | 'physicalSocket'
    >
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-channel-physical-socket-create.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<
      CreatePhysicalSocketModel,
      'chargePointId' | 'channelId' | 'physicalSocket'
    >
  > = (createPhysicalSocket) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    // on récupère la première place dispo dans la liste des physical sockets
    const ids = new Set(
      chargePoint.channels.flatMap((c) =>
        c.physicalSockets.map((s) => s.ocppConnectorId),
      ),
    );
    let newOcppConnectorId = 1;
    while (ids.has(newOcppConnectorId)) newOcppConnectorId += 1;

    createPhysicalSocketMutation({
      ...createPhysicalSocket,
      physicalSocket: {
        ...createPhysicalSocket.physicalSocket,
        ocppConnectorId: newOcppConnectorId,
      },
    })
      .unwrap()
      .then(() => {
        toast.success(
          t('$.charge-point-channel-physical-socket-create.success'),
        );
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-channel-physical-socket-create.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getCreatePhysicalSocketDefaultValues(chargePoint.id, channel.id);

  return (
    <CreateChargePointChannelPhysicalSocketContextProvider data={data}>
      <ChargePointChannelPhysicalSocketCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </CreateChargePointChannelPhysicalSocketContextProvider>
  );
};
